import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import clsx from 'clsx';
import { toast } from 'sonner';
import { Control, FieldErrors, UseFormWatch } from 'react-hook-form';
import { CreateRoomType, CreateRoomTypeSchema } from '../schema/CreateRoomTypeSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCreateRoomType } from '../hooks/useCreateRoomType';
import { applyServerValidationErrors } from '../../../shared/utils/serverValidation';

interface CreateRoomTypeDialogProps {
    show: boolean;
    onClose: () => void;
}

const CreateRoomTypeDialog: React.FC<CreateRoomTypeDialogProps> = ({ show, onClose }) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        reset,
        watch,
        setError
    } = useForm<CreateRoomType>({
        resolver: zodResolver(CreateRoomTypeSchema),
        defaultValues: {
            name: '',
            total_rooms: 0,
            room_number: [],
            description: '',
            occ_adults: 0,
            occ_children: 0
        }
    });

    const handleClose = () => {
        reset();
        onClose();
    };

    const { mutate, isPending } = useCreateRoomType();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((values: any) => {
        toast.loading('Creating room type...', { id: 'create-room-type' });
        mutate(values, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['rooms', 'rates'].includes(key as string))
                });

                toast.success('Room type created successfully', { id: 'create-room-type' });
            },
            onError: (mutationError: any) => {
                const handled = applyServerValidationErrors(mutationError, setError, {
                    toastId: 'create-room-type',
                    knownFields: ['name', 'description', 'occ_adults', 'occ_children', 'total_rooms'],
                    fieldErrorToastMessage: 'Failed to create room type',
                });

                if (!handled) {
                    toast.error(mutationError?.response?.data?.message || 'Failed to create room type', { id: 'create-room-type' });
                }
            }
        });
    }, [mutate]);

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" className="offcanvas-responsive">
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Create Room Type</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column" style={{ height: '100%' }}>
                    <div className="action-sheet-content flex-grow-1">
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-md-end" style={{ fontSize: '14px' }}>Name: </label>
                            <div className="col-sm-8">
                                <input
                                    type="text"
                                    className={clsx("form-control app-form-control rounded-1", errors.name && 'is-invalid')}
                                    id="name"
                                    placeholder="Name"
                                    {...register('name', { required: 'Name is required' })}
                                    style={{ fontSize: '14px', height: '35px' }}
                                />
                                {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label htmlFor="description" className="col-sm-4 col-form-label d-flex justify-content-md-end">Description: </label>
                            <div className="col-sm-8">
                                <textarea
                                    className={clsx("form-control app-form-control rounded-1", errors.description && 'is-invalid')}
                                    id="description"
                                    placeholder="Enter rate plan description"
                                    {...register('description')}
                                />
                                {errors.description && <div className="invalid-feedback">{errors.description.message}</div>}
                            </div>
                        </div>

                        <h3 className="mt-6 border-bottom fw-normal">Room Settings</h3>

                        {/* Add Adults input */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="adults" className="col-sm-4 col-form-label d-flex justify-content-md-end">Occupancy Adults</label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors.occ_adults && 'is-invalid')}
                                    id="adults"
                                    placeholder="Enter no. of adults"
                                    {...register('occ_adults', { required: 'Adults number is required', min: 0 })}
                                />
                                {errors.occ_adults && <div className="invalid-feedback">{errors.occ_adults.message}</div>}
                            </div>
                        </div>

                        {/* Add children input */}
                        <div className="row mb-3 align-items-center">
                            <label htmlFor="children" className="col-sm-4 col-form-label d-flex justify-content-md-end">Occupancy Children</label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors.occ_children && 'is-invalid')}
                                    id="children"
                                    placeholder="Enter no. of children"
                                    {...register('occ_children', { required: 'Children number is required', min: 0 })}
                                />
                                {errors.occ_children && <div className="invalid-feedback">{errors.occ_children.message}</div>}
                            </div>
                        </div>

                        <div className="row mb-3 align-items-center">
                            <label htmlFor="total_rooms" className="col-sm-4 col-form-label d-flex justify-content-md-end">Set the no. room Available</label>
                            <div className="col-sm-8">
                                <input
                                    type="number"
                                    className={clsx("form-control app-form-control rounded-1", errors.total_rooms && 'is-invalid')}
                                    id="total_rooms"
                                    placeholder="Enter no. room available"
                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    {...register('total_rooms', {
                                        required: 'Total rooms is required', // Validasi required
                                        min: {
                                            value: 1, // Minimum nilai harus 1
                                            message: 'Total rooms must be at least 1', // Pesan error jika kurang dari 1
                                        },
                                    })}
                                />
                                {errors.total_rooms && <div className="invalid-feedback">{errors.total_rooms.message}</div>}
                            </div>
                        </div>

                        <RoomNumbers watch={watch} control={control} errors={errors} />
                    </div>

                    <div className="form-group mt-auto d-grid">
                        <button type="submit" className="btn btn-primary btn-block" disabled={isPending}>
                            {isPending ? 'Creating...' : 'Create Room Type'}
                        </button>
                    </div>
                </form>
            </Offcanvas.Body>
        </Offcanvas>
    );
}

export default CreateRoomTypeDialog;

interface RoomNumbersProps {
    watch: UseFormWatch<any>;
    control: Control<any>;
    errors: FieldErrors<any>;
}

const RoomNumbers: React.FC<RoomNumbersProps> = ({ watch, control, errors }) => {
    const { fields, replace } = useFieldArray({
        control,
        name: 'room_number', // Array hanya berisi string
    });

    // Watch total_rooms
    const totalRooms = watch('total_rooms', 0);

    // Handle room count change
    useEffect(() => {
        const currentRoomCount = fields.length;

        if (totalRooms > currentRoomCount) {
            const newRooms = Array.from({ length: totalRooms }, (_, i) => `${101 + i}`);
            replace(newRooms); // Gantilah seluruh array agar tetap sinkron
        } else if (totalRooms < currentRoomCount) {
            replace(fields.slice(0, totalRooms).map((field) => field)); // Kurangi yang tidak perlu
        }
    }, [totalRooms, replace, fields]);

    return (
        <>
            {fields.map((item, index) => (
                <div className="row mb-3 align-items-center" key={index}>
                    <label htmlFor={`room_number_${index}`} className="col-sm-4 col-form-label d-flex justify-content-end">
                        Room {index + 1} Name
                    </label>
                    <div className="col-sm-8">
                        <Controller
                            name={`room_number.${index}`} // Array hanya berisi string
                            control={control}
                            defaultValue={item} // Default ke nomor kamar yang sudah ada
                            rules={{ required: `Room ${index + 1} name is required` }}
                            render={({ field }) => (
                                <input
                                    type="text"
                                    {...field}
                                    id={`room_number_${index}`}
                                    className={clsx("form-control app-form-control rounded-1", (errors.room_number as any)?.[index] && 'is-invalid')}
                                    placeholder={`Enter name for room ${index + 1}`}
                                />
                            )}
                        />
                        {(errors?.room_number as any)?.[index] && (
                            <div className="invalid-feedback">
                                {Array.isArray(errors.room_number) && errors.room_number[index]?.message}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </>
    )
};